import { ShieldCheck, ShieldAlert, ShieldX, Info } from 'lucide-react';

const ICONS = {
  safe: ShieldCheck,
  caution: ShieldAlert,
  high: ShieldX,
  unknown: Info,
};

const STYLES = {
  safe: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
  caution: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
  high: 'bg-red-500/15 border-red-500/40 text-red-300',
  unknown: 'bg-slate-700/40 border-slate-600 text-slate-300',
};

const LABELS = {
  safe: 'NFT Verified',
  caution: 'Caution',
  high: 'High Risk',
  unknown: 'Unverified',
};

const FLAG_DESCRIPTIONS = {
  host_does_not_own_nft: 'Host wallet no longer owns this NFT',
  malformed_token_id: 'Invalid NFT token format',
  self_issued_nft: 'Issuer is the host themselves',
  previously_listed_1x: 'NFT was previously listed once',
};

function flagLabel(flag) {
  if (FLAG_DESCRIPTIONS[flag]) return FLAG_DESCRIPTIONS[flag];
  if (flag.startsWith('new_issuer_account_')) {
    return `Issuer wallet only ${flag.split('_').pop()} old`;
  }
  if (flag.startsWith('low_issuer_activity_')) {
    return `Issuer has very few transactions`;
  }
  if (flag.startsWith('previously_listed_')) {
    const n = flag.split('_').pop().replace('x', '');
    return `Previously listed ${n}× on this platform`;
  }
  return flag.replace(/_/g, ' ');
}

/**
 * Compact NFT trust badge for cards.
 */
export function NftTrustBadge({ analysis }) {
  if (!analysis) return null;
  const level = analysis.risk_level || 'unknown';
  const Icon = ICONS[level];
  return (
    <span
      data-testid={`nft-trust-${level}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STYLES[level]}`}
      title={analysis.risk_flags?.length ? analysis.risk_flags.map(flagLabel).join(' • ') : LABELS[level]}
    >
      <Icon className="w-2.5 h-2.5" />
      {LABELS[level]}
    </span>
  );
}

/**
 * Full panel for GameRoom / AuctionRoom showing all NFT signals.
 */
export function NftTrustPanel({ analysis }) {
  if (!analysis || !analysis.issuer) return null;
  const level = analysis.risk_level || 'unknown';
  const Icon = ICONS[level];

  return (
    <div data-testid="nft-trust-panel" className={`rounded-xl border p-3 space-y-2 ${STYLES[level]}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <p className="text-xs font-bold uppercase tracking-wide">NFT Trust Check — {LABELS[level]}</p>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <p className="opacity-70 text-[9px] uppercase">Issuer</p>
          <p className="font-mono truncate">{analysis.issuer.slice(0, 6)}…{analysis.issuer.slice(-4)}</p>
        </div>
        {analysis.issuer_age_days != null && (
          <div>
            <p className="opacity-70 text-[9px] uppercase">Issuer age</p>
            <p className="font-semibold">{analysis.issuer_age_days} days</p>
          </div>
        )}
        {analysis.issuer_tx_count != null && (
          <div>
            <p className="opacity-70 text-[9px] uppercase">Issuer txns</p>
            <p className="font-semibold">{analysis.issuer_tx_count}</p>
          </div>
        )}
        <div>
          <p className="opacity-70 text-[9px] uppercase">Host owns</p>
          <p className="font-semibold">{analysis.host_owns ? 'Yes' : 'No'}</p>
        </div>
      </div>
      {analysis.risk_flags && analysis.risk_flags.length > 0 && (
        <ul className="space-y-0.5 pt-1 border-t border-current/20 text-[10px]">
          {analysis.risk_flags.map((f) => (
            <li key={f} data-testid={`nft-flag-${f}`}>• {flagLabel(f)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default NftTrustBadge;
